<template>
  <div class="app-layout">
    <Sidebar />
    <div class="lessons-container">
      <!-- Header Component -->
      <LessonsHeader 
        :total-lessons="filteredLessons.length"
        :total-duration="totalDuration"
        :current-view="currentView"
        :selected-count="selectedLessons.size"
        :geo-restrictions-active="geoRestrictionsActive"
        @view-change="currentView = $event"
        @create-lesson="$router.push('/lessons/new')"
        @refresh="fetchLessons"
        @bulk-move="showBulkMoveDialog = true"
        @bulk-status="showBulkStatusDialog = true"
        @bulk-export="exportSelectedLessons"
        @bulk-delete="deleteSelectedLessons"
        @toggle-geo-restrictions="toggleGeoRestrictionsDialog"
      />

      <!-- Search and Filters Component -->
      <LessonsFilters 
        :unique-courses="uniqueCourses"
        @search-update="handleSearchUpdate"
        @filters-update="updateFilters"
        @filters-reset="resetFilters"
      />

      <div v-if="loading" class="loading-state">
        Loading lessons...
      </div>
      
      <div v-else-if="filteredLessons.length === 0" class="empty-state">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="empty-icon">
          <path d="M10 21h4"/>
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
          <path d="M17 8l-5-5-5 5"/>
          <path d="M12 3v12"/>
        </svg>
        <p>No lessons found matching your criteria</p>
        <button @click="resetFilters" class="reset-button">Reset Filters</button>
      </div>
      
      <div v-else>
        <!-- Grid View Component -->
        <LessonsGrid 
          v-if="currentView === 'grid'"
          :filtered-lessons="filteredLessons"
          :selected-lessons="selectedLessons"
          @toggle-selection="toggleLessonSelection"
          @toggle-select-all="toggleSelectAll"
          @play-lesson="playLesson"
          @view-lesson="viewLessonDetails"
          @edit-lesson="editLesson"
          @delete-lesson="confirmDeleteLesson"
          @bulk-move="showBulkMoveDialog = true"
          @bulk-status="showBulkStatusDialog = true"
          @bulk-export="exportSelectedLessons"
          @bulk-delete="deleteSelectedLessons"
        />

        <!-- Kanban View Component -->
        <LessonsKanban 
          v-else-if="currentView === 'kanban'"
          :filtered-lessons="filteredLessons"
          @play-lesson="playLesson"
          @edit-lesson="editLesson"
          @update-status="updateLessonStatus"
        />

        <!-- Analytics View Component -->
        <LessonsAnalytics 
          v-else-if="currentView === 'analytics'"
          :filtered-lessons="filteredLessons"
          @refresh-analytics="refreshAnalytics"
        />

        <!-- Table View Component -->
        <LessonsTable 
          v-else-if="currentView === 'table'"
          :filtered-lessons="filteredLessons"
          :selected-lessons="selectedLessons"
          :show-select-all="true"
          @toggle-selection="toggleLessonSelection"
          @toggle-select-all="toggleSelectAll"
          @play-lesson="playLesson"
          @view-lesson="viewLessonDetails"
          @edit-lesson="editLesson"
          @delete-lesson="confirmDeleteLesson"
          @bulk-move="showBulkMoveDialog = true"
          @bulk-status="showBulkStatusDialog = true"
          @bulk-export="exportSelectedLessons"
          @bulk-delete="deleteSelectedLessons"
        />
      </div>

      <!-- Edit Lesson Component -->
      <div class="edit-lesson-wrapper" v-if="editingLesson">
        <EditLesson 
          :lesson="editingLesson"
          :courses="courses"
          @close="editingLesson = null"
          @save="saveLessonEdit"
        />
      </div>

      <!-- Delete Dialog Component -->
      <LessonsDeleteDialog 
        v-if="showDeleteDialog"
        :show="showDeleteDialog"
        :lesson="deletingLesson"
        @close="showDeleteDialog = false"
        @confirm="deleteLesson"
      />

      <!-- Bulk Move Dialog Component -->
      <LessonsBulkMoveDialog 
        v-if="showBulkMoveDialog"
        :show="showBulkMoveDialog"
        :selected-lessons="Array.from(selectedLessons)"
        :courses="courses"
        @close="showBulkMoveDialog = false"
        @move="bulkMoveLessons"
      />

      <!-- Bulk Status Dialog Component -->
      <LessonsBulkStatusDialog 
        v-if="showBulkStatusDialog"
        :show="showBulkStatusDialog"
        :selected-lessons="Array.from(selectedLessons)"
        @close="showBulkStatusDialog = false"
        @update="bulkUpdateStatus"
      />

      <!-- Geo Restrictions Dialog Component -->
      <LessonsGeoRestrictionsDialog 
        v-if="showGeoRestrictionsDialog"
        :show="showGeoRestrictionsDialog"
        :selected-lessons="Array.from(selectedLessons)"
        :current-restrictions="currentGeoRestrictions"
        @close="showGeoRestrictionsDialog = false"
        @save="saveGeoRestrictions"
      />

      <!-- Pagination Controls -->
      <div class="pagination-controls" v-if="pagination.total > pagination.perPage">
        <button 
          @click="pagination.currentPage--; fetchLessons()" 
          :disabled="pagination.currentPage === 1"
        >
          Previous
        </button>
        <span>Page {{ pagination.currentPage }} of {{ totalPages }}</span>
        <button 
          @click="pagination.currentPage++; fetchLessons()" 
          :disabled="pagination.currentPage >= totalPages"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';
import LessonsHeader from './lessons/LessonsHeader.vue';
import LessonsFilters from './lessons/LessonsFilters.vue';
import LessonsGrid from './lessons/LessonsGrid.vue';
import LessonsKanban from './lessons/LessonsKanban.vue';
import LessonsAnalytics from './lessons/LessonsAnalytics.vue';
import LessonsDeleteDialog from './lessons/LessonsDeleteDialog.vue';
import EditLesson from './lessons/EditLesson.vue';
import LessonsBulkMoveDialog from './lessons/LessonsBulkMoveDialog.vue';
import LessonsBulkStatusDialog from './lessons/LessonsBulkStatusDialog.vue';
import LessonsTable from './lessons/LessonsTable.vue';
import LessonsGeoRestrictionsDialog from './lessons/LessonsGeoRestrictionsDialog.vue';
import { filterAccessibleLessons, getUserLocation, checkGeoRestrictions } from '../utils/geoRestrictions';

export default {
  name: 'CourseLessons',
  components: {
    Sidebar,
    LessonsHeader,
    LessonsFilters,
    LessonsGrid,
    LessonsKanban,
    LessonsAnalytics,
    LessonsDeleteDialog,
    EditLesson,
    LessonsBulkMoveDialog,
    LessonsBulkStatusDialog,
    LessonsTable,
    LessonsGeoRestrictionsDialog
  },
  data() {
    return {
      lessons: [],
      loading: true,
      error: null,
      showEditDialog: false,
      showDeleteDialog: false,
      showBulkMoveDialog: false,
      showBulkStatusDialog: false,
      showGeoRestrictionsDialog: false,
      geoRestrictionsActive: false,
      currentGeoRestrictions: {
        enabled: false,
        restrictionType: 'blocklist',
        countries: [],
        ipRanges: [],
        blockMessage: 'This content is not available in your region.'
      },
      editingLesson: null,
      deletingLesson: null,
      isSubmitting: false,
      searchQuery: '',
      statusFilter: '',
      courseFilter: '',
      difficultyFilter: '',
      durationFilter: '',
      ratingFilter: '',
      courses: [],
      pagination: {
        currentPage: 1,
        perPage: 10,
        total: 0
      },
      selectedLessons: new Set(),
      currentView: 'grid'
    };
  },
  computed: {
    totalDuration() {
      return this.lessons.reduce((acc, lesson) => acc + (lesson.duration || 0), 0);
    },
    uniqueCourses() {
      return [...new Set(this.lessons.map(lesson => lesson.course?.title).filter(Boolean))];
    },
    filteredLessons() {
      return this.lessons;
    },
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.perPage);
    }
  },
  methods: {
    async fetchLessons() {
      try {
        this.loading = true;
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.kewaar.com/api/admin/all-lessons', {
          params: {
            page: this.pagination.currentPage,
            per_page: this.pagination.perPage,
            search: this.searchQuery
          },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        // Get raw lessons from API
        let lessons = response.data.data.lessons;
        
        // Apply geolocation restrictions filtering for non-admin users
        // Admin users see all lessons regardless of restrictions
        const userIsAdmin = localStorage.getItem('userRole') === 'admin';
        if (!userIsAdmin) {
          lessons = await filterAccessibleLessons(lessons);
        }
        
        this.lessons = lessons;
        this.pagination.total = response.data.data.total;
      } catch (error) {
        console.error('Error fetching lessons:', error);
      } finally {
        this.loading = false;
      }
    },
    async fetchCourses() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.kewaar.com/api/admin/courses', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        this.courses = response.data.data.courses;
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    },
    updateFilters(filters) {
      this.statusFilter = filters.status;
      this.courseFilter = filters.course;
      this.difficultyFilter = filters.difficulty;
      this.durationFilter = filters.duration;
      this.ratingFilter = filters.rating;
    },
    resetFilters() {
      this.searchQuery = '';
      this.statusFilter = '';
      this.courseFilter = '';
      this.difficultyFilter = '';
      this.durationFilter = '';
      this.ratingFilter = '';
    },
    async playLesson(lesson) {
      // Check geolocation restrictions before playing the lesson
      const userIsAdmin = localStorage.getItem('userRole') === 'admin';
      
      if (!userIsAdmin && lesson.geo_restrictions && lesson.geo_restrictions.enabled) {
        const userLocation = await getUserLocation();
        const { allowed, message } = checkGeoRestrictions(lesson, userLocation);
        
        if (!allowed) {
          alert(message || 'This content is not available in your region.');
          return;
        }
      }
      
      window.open(lesson.lesson_url, '_blank');
    },
    editLesson(lesson) {
      // Create a deep copy of the lesson to preserve all nested data
      this.editingLesson = JSON.parse(JSON.stringify(lesson));
    },
    confirmDeleteLesson(lesson) {
      this.deletingLesson = lesson;
      this.showDeleteDialog = true;
    },
    async deleteLesson(lessonId) {
      try {
        this.isSubmitting = true;
        const token = localStorage.getItem('token');
        
        await axios.delete(`https://api.kewaar.com/api/admin/lessons1/${lessonId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        this.lessons = this.lessons.filter(l => l.id !== lessonId);
        this.showDeleteDialog = false;
        this.deletingLesson = null;
        
      } catch (error) {
        console.error('Error deleting lesson:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async saveLessonEdit(formData) {
      try {
        this.isSubmitting = true;
        const token = localStorage.getItem('token');
        
        // Ensure all required fields are present and preserve existing data
        const lessonData = {
          ...this.editingLesson, // Preserve all existing data first
          ...formData, // Override with new form data
          // Ensure these fields are properly formatted
          duration: parseInt(formData.duration) || this.editingLesson.duration || 0,
          course_id: formData.course_id || this.editingLesson.course_id,
          lesson_status: formData.lesson_status || this.editingLesson.lesson_status,
          // Preserve these fields if they exist in the original lesson
          lesson_video_id: this.editingLesson.lesson_video_id,
          lesson_url: this.editingLesson.lesson_url,
          lesson_thumbnail_url: this.editingLesson.lesson_thumbnail_url,
          downloadable_url: this.editingLesson.downloadable_url,
          order: this.editingLesson.order,
          is_hidden: this.editingLesson.is_hidden
        };
        
        const response = await axios.put(
          `https://api.kewaar.com/api/admin/lessons1/${this.editingLesson.id}`,
          lessonData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          const index = this.lessons.findIndex(l => l.id === this.editingLesson.id);
          if (index !== -1) {
            this.lessons[index] = response.data.data;
          }
          this.showEditDialog = false;
          this.editingLesson = null;
        } else {
          throw new Error(response.data.message || 'Failed to update lesson');
        }
      } catch (error) {
        console.error('Error saving lesson:', error);
        alert(error.response?.data?.message || 'Failed to save lesson. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    },
    toggleSelectAll() {
      if (this.selectedLessons.size === this.filteredLessons.length) {
        this.selectedLessons.clear();
      } else {
        this.filteredLessons.forEach(lesson => {
          this.selectedLessons.add(lesson.id);
        });
      }
    },
    toggleLessonSelection(lessonId) {
      if (this.selectedLessons.has(lessonId)) {
        this.selectedLessons.delete(lessonId);
      } else {
        this.selectedLessons.add(lessonId);
      }
    },
    viewLessonDetails(lesson) {
      this.$router.push(`/lessons/${lesson.id}`);
    },
    async updateLessonStatus(lessonId, newStatus) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`https://api.kewaar.com/api/admin/lessons1/${lessonId}`, 
          { lesson_status: newStatus },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          }
        );
        
        const index = this.lessons.findIndex(l => l.id === lessonId);
        if (index !== -1) {
          this.lessons[index].lesson_status = newStatus;
        }
      } catch (error) {
        console.error('Error updating lesson status:', error);
      }
    },
    async bulkMoveLessons(courseId) {
      try {
        this.isSubmitting = true;
        const token = localStorage.getItem('token');
        await axios.post('https://api.kewaar.com/api/admin/lessons1/bulk-move', {
          lesson_ids: Array.from(this.selectedLessons),
          course_id: courseId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        this.lessons = this.lessons.map(lesson => {
          if (this.selectedLessons.has(lesson.id)) {
            const targetCourse = this.courses.find(c => c.id === courseId);
            return {
              ...lesson,
              course_id: courseId,
              course: targetCourse
            };
          }
          return lesson;
        });
        
        this.showBulkMoveDialog = false;
        this.selectedLessons.clear();
      } catch(error) {
        console.error('Bulk move error:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async bulkUpdateStatus(newStatus) {
      try {
        this.isSubmitting = true;
        const token = localStorage.getItem('token');
        await axios.post('https://api.kewaar.com/api/admin/lessons1/bulk-status-update', {
          lesson_ids: Array.from(this.selectedLessons),
          status: newStatus
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        this.lessons = this.lessons.map(lesson => {
          if (this.selectedLessons.has(lesson.id)) {
            return {
              ...lesson,
              lesson_status: newStatus
            };
          }
          return lesson;
        });
        
        this.showBulkStatusDialog = false;
        this.selectedLessons.clear();
      } catch(error) {
        console.error('Bulk status update error:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async deleteSelectedLessons() {
      try {
        const token = localStorage.getItem('token');
        await axios.post('https://api.kewaar.com/api/admin/lessons1/batch-delete', {
          lesson_ids: Array.from(this.selectedLessons)
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        this.lessons = this.lessons.filter(l => !this.selectedLessons.has(l.id));
        this.selectedLessons.clear();
      } catch(error) {
        console.error('Batch delete error:', error);
      }
    },
    exportSelectedLessons() {
      const selectedLessonData = this.lessons
        .filter(lesson => this.selectedLessons.has(lesson.id))
        .map(lesson => ({
          id: lesson.id,
          title: lesson.title,
          description: lesson.description,
          status: lesson.lesson_status,
          duration: lesson.duration,
          course: lesson.course?.title || 'N/A'
        }));
      
      const dataStr = JSON.stringify(selectedLessonData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute('download', `lessons-export-${new Date().toISOString().slice(0, 10)}.json`);
      a.click();
      
      URL.revokeObjectURL(url);
    },
    refreshAnalytics() {
      this.fetchLessons();
    },
    async handleSearchUpdate(query) {
      this.searchQuery = query;
      this.pagination.currentPage = 1; // Reset to first page when searching
      await this.fetchLessons();
    },
    toggleGeoRestrictionsDialog() {
      this.showGeoRestrictionsDialog = true;
    },
    async saveGeoRestrictions(restrictions) {
      try {
        this.isSubmitting = true;
        const token = localStorage.getItem('token');
        
        // If we have selected lessons, apply restrictions to those lessons
        if (this.selectedLessons.size > 0) {
          await axios.post('https://api.kewaar.com/api/admin/lessons/geo-restrictions', {
            lesson_ids: Array.from(this.selectedLessons),
            restrictions: restrictions
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Update local data
          this.selectedLessons.forEach(lessonId => {
            const lesson = this.lessons.find(l => l.id === lessonId);
            if (lesson) {
              lesson.geo_restrictions = restrictions;
            }
          });
        } else {
          // If no lessons selected, apply as global default for new lessons
          localStorage.setItem('defaultGeoRestrictions', JSON.stringify(restrictions));
        }
        
        this.currentGeoRestrictions = restrictions;
        this.geoRestrictionsActive = restrictions.enabled;
        this.showGeoRestrictionsDialog = false;
      } catch(error) {
        console.error('Error saving geo restrictions:', error);
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  async created() {
    this.axiosInstance = axios.create({
      baseURL: 'https://api.kewaar.com/api',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });

    this.axiosInstance.interceptors.response.use(
      response => response,
      error => {
        if (error.response.status === 401) {
          this.$router.push('/login');
        }
        return Promise.reject(error);
      }
    );

    await Promise.all([
      this.fetchLessons(),
      this.fetchCourses()
    ]);
  }
};
</script>

<style scoped>
.app-layout {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
}

.lessons-container {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
  max-width: 1400px;
}

.loading-state {
  text-align: center;
  padding: 3rem;
  color: #64748b;
  font-size: 1rem;
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.empty-icon {
  width: 48px;
  height: 48px;
  color: #64748b;
  margin-bottom: 1rem;
}

.empty-state p {
  color: #475569;
  font-size: 1rem;
  margin: 0 0 1.5rem;
}

.reset-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  background: #2563eb;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.reset-button:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.pagination-controls {
  margin-top: 1.5rem;
  text-align: center;
}

.pagination-controls button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0 0.5rem;
}

.pagination-controls button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 1024px) {
  .lessons-container {
    margin-left: 0;
    padding: 1rem;
  }
}

.edit-lesson-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
}

.edit-lesson-wrapper :deep(.edit-lesson-container) {
  margin: 2rem auto;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
  position: relative;
}
</style> 